'use client'

import { Accordion as AccordionPrimitive, Slot } from 'radix-ui'
import { Chevron } from '@/icons'
import { cn } from '@/utils'

export type AccordionProps<T extends React.ElementType = 'div'> = {
  as?: T
  className?: string
} & Partial<React.ComponentProps<typeof AccordionPrimitive.Root>> &
  React.ComponentPropsWithoutRef<T>

/**
 * Accordion component that contains the Item, Trigger, Content and Chevron components.
 */
const Accordion = <T extends React.ElementType = 'div'>({
  as,
  type,
  children,
  ...props
}: AccordionProps<T>) => {
  const Component = (as || Slot.Root) as React.ElementType

  return (
    <AccordionPrimitive.Root type={type || 'single'} asChild={!!as} {...props}>
      <Component>{children}</Component>
    </AccordionPrimitive.Root>
  )
}

Accordion.displayName = AccordionPrimitive.Root.displayName

export type AccordionItemProps<T extends React.ElementType = 'div'> = {
  as?: T
  className?: string
} & Omit<React.ComponentProps<typeof AccordionPrimitive.Item>, 'asChild'> &
  React.ComponentPropsWithoutRef<T>

/**
 * Item section for Accordion.
 */
const AccordionItem = <T extends React.ElementType>({
  as,
  ref,
  children,
  className,
  ...props
}: AccordionItemProps<T>) => {
  const Component = (as || Slot.Root) as React.ElementType

  return (
    <AccordionPrimitive.Item
      ref={ref}
      asChild={!!as}
      className={cn('group overflow-hidden', className)}
      {...props}
    >
      <Component>{children}</Component>
    </AccordionPrimitive.Item>
  )
}

AccordionItem.displayName = AccordionPrimitive.Item.displayName

export type AccordionTriggerProps<T extends React.ElementType = 'div'> = {
  as?: T
  className?: string
} & Omit<React.ComponentProps<typeof AccordionPrimitive.Trigger>, 'asChild'> &
  React.ComponentPropsWithoutRef<T>

/**
 * Trigger section for Accordion.
 */
const AccordionTrigger = <T extends React.ElementType>({
  as,
  ref,
  children,
  className,
  ...props
}: AccordionTriggerProps<T>) => {
  const Component = (as || Slot.Root) as React.ElementType

  return (
    <AccordionPrimitive.Trigger
      ref={ref}
      asChild={!!as}
      className={cn('cursor-pointer', className)}
      {...props}
    >
      <Component>{children}</Component>
    </AccordionPrimitive.Trigger>
  )
}

AccordionTrigger.displayName = AccordionPrimitive.Trigger.displayName

export type AccordionContentProps<T extends React.ElementType = 'div'> = {
  as?: T
  className?: string
} & Omit<React.ComponentProps<typeof AccordionPrimitive.Content>, 'asChild'> &
  React.ComponentPropsWithoutRef<T>

/**
 * Content section for Accordion.
 */
const AccordionContent = <T extends React.ElementType>({
  as,
  ref,
  children,
  className,
  ...props
}: AccordionContentProps<T>) => {
  const Component = (as || Slot.Root) as React.ElementType

  return (
    <AccordionPrimitive.Content
      ref={ref}
      asChild={!!as}
      className={cn(
        'data-[state=closed]:animate-accordion-up',
        'data-[state=open]:animate-accordion-down',
        className,
      )}
      {...props}
    >
      <Component>{children}</Component>
    </AccordionPrimitive.Content>
  )
}

AccordionContent.displayName = AccordionPrimitive.Content.displayName

/**
 * Crevron for List Items used in Accordion.
 */
const AccordionChevron = ({
  className,
  ...props
}: React.ComponentProps<typeof Chevron>) => {
  return (
    <Chevron
      className={cn(
        'group-data-[state=open]:rotate-270 h-[6px] w-[12px] transition-transform duration-500 group-data-[state=closed]:rotate-90',
        className,
      )}
      aria-hidden="true"
      {...props}
    />
  )
}

AccordionChevron.displayName = 'AccordionChevron'

export {
  Accordion,
  AccordionChevron,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
}
